import React from 'react'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import Radio from '@material-ui/core/Radio'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import SupervisorAccountOutlinedIcon from 'components/material-symbol/icons/SupervisorAccount'
import TipWell from 'components/tip-well'
import InfoIcon from 'components/material-symbol/icons/Info'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { useFormContext } from 'react-hook-form'
import useStyles from './styles'

const formatErrorMessage = (name, message) => {
  if (!message) return null
  const error = message
    .split(' ')
    .slice(1)
    .join(' ')
  return `${name} ${error}`
}

const fieldProps = classes => ({
  variant: 'outlined',
  fullWidth: true,
  className: classes.formField,
})

export const StudentNameFields = () => {
  const classes = useStyles()
  const { register, errors } = useFormContext()
  const sharedFieldProps = fieldProps(classes)

  return (
    <Box className={classes.fieldRow}>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="firstName"
        >
          First name *
        </Typography>
        <TextField
          name="firstName"
          id="firstName"
          inputRef={register}
          error={!!errors.firstName}
          helperText={formatErrorMessage(
            'First name',
            errors.firstName?.message,
          )}
          {...sharedFieldProps}
        />
      </FormControl>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="lastName"
        >
          Last name *
        </Typography>
        <TextField
          name="lastName"
          id="lastName"
          inputRef={register}
          error={!!errors.lastName}
          helperText={formatErrorMessage('Last name', errors.lastName?.message)}
          {...sharedFieldProps}
        />
      </FormControl>
    </Box>
  )
}

export const ContactInfoFields = () => {
  const classes = useStyles()
  const { register, errors } = useFormContext()
  const sharedFieldProps = fieldProps(classes)

  return (
    <Box className={classes.fieldRow}>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="email"
        >
          Email
        </Typography>
        <TextField
          name="email"
          id="email"
          inputRef={register}
          error={!!errors.email}
          helperText={formatErrorMessage('Email', errors.email?.message)}
          {...sharedFieldProps}
        />
      </FormControl>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="phone"
        >
          Phone number
        </Typography>
        <TextField
          name="phone"
          id="phone"
          inputRef={register}
          onBlur={e => {
            e.target.value = formatPhoneNumber(e.target.value)
          }}
          error={!!errors.phone}
          helperText={formatErrorMessage('Phone', errors.phone?.message)}
          {...sharedFieldProps}
        />
      </FormControl>
    </Box>
  )
}

export const GuardianNameFields = () => {
  const classes = useStyles()
  const { register, errors } = useFormContext()
  const sharedFieldProps = fieldProps(classes)

  return (
    <Box className={classes.fieldRow}>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="guardianFirstName"
        >
          First name *
        </Typography>
        <TextField
          name="guardianFirstName"
          id="guardianFirstName"
          inputRef={register}
          error={!!errors.guardianFirstName}
          helperText={formatErrorMessage(
            'First name',
            errors.guardianFirstName?.message,
          )}
          {...sharedFieldProps}
        />
      </FormControl>
      <FormControl className={classes.fieldGroup}>
        <Typography
          className={classes.fieldLabel}
          component="label"
          htmlFor="guardianLastName"
        >
          Last name *
        </Typography>
        <TextField
          name="guardianLastName"
          id="guardianLastName"
          inputRef={register}
          error={!!errors.guardianLastName}
          helperText={formatErrorMessage(
            'Last name',
            errors.guardianLastName?.message,
          )}
          {...sharedFieldProps}
        />
      </FormControl>
    </Box>
  )
}

const AgeChoiceCard = ({
  title,
  description,
  selected,
  disabled,
  icon: Icon,
  testId,
}) => {
  const classes = useStyles()

  return (
    <Box
      className={clsx(
        classes.ageChoiceCard,
        disabled && classes.ageChoiceCardDisabled,
      )}
      data-testid={testId}
      aria-pressed={selected}
    >
      <Box className={classes.ageChoiceIcon}>
        <Icon />
      </Box>
      <Box className={classes.ageChoiceBody}>
        <Typography className={classes.ageChoiceTitle}>{title}</Typography>
        {description ? (
          <Typography className={classes.ageChoiceDescription}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Radio
        checked={selected}
        disabled={disabled}
        tabIndex={-1}
        color="default"
        inputProps={{ 'aria-hidden': true }}
        classes={{
          root: classes.ageChoiceRadioRoot,
          checked: classes.ageChoiceRadioChecked,
        }}
      />
    </Box>
  )
}

export const AgeTiles = ({ isMinor }) => {
  const classes = useStyles()

  return (
    <Box className={classes.fieldGroup}>
      <Typography className={classes.fieldLabel}>
        Is this student an adult (18+) or a junior (under 18)?
      </Typography>
      <Box className={classes.ageChoiceGroup}>
        <AgeChoiceCard
          title="Adult"
          description="Student is 18 years or older."
          selected={!isMinor}
          disabled
          icon={PersonOutlineIcon}
          testId="student-age-adult-tile"
        />
        <Divider className={classes.ageChoiceDivider} />
        <AgeChoiceCard
          title="Junior"
          description="Student is under 18 years old."
          selected={isMinor}
          disabled
          icon={SupervisorAccountOutlinedIcon}
          testId="student-age-junior-tile"
        />
      </Box>
      <TipWell
        icon={InfoIcon}
        caption="The student's age category is set when they are added and can't be changed later. This determines whether guardian information is required."
      />
    </Box>
  )
}
